import { LitElement, html, css, nothing, PropertyValues, TemplateResult } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { VesselPlanningModel, VoyagePlan } from '../../../src/models/vessel-planning-model';
import { ScheduleEntry } from '../../../src/models/types/voyage-schedules-model.types';
import { VoyageDialogPopupManager } from '../../voyage-dialog-popup/src/voyage-dialog-popup-manager';
import { WeatherRoutingModel } from '../../../src/models/weather-routing-reports-model';
import { VesselClfPopupModel } from '../../../src/models/vessel-clf-popup-model';
import { VoyageRoutingModel } from '../../../src/models/voyage-routing-model';
import { VoyageWeatherRoutingModel } from '../../../src/models/weather-routing-model';
import { EtaRequestDeactivated, ScheduleChangeModel } from '../../../src/models/schedule-change-model';
import { formatEtaDate, formatUTCDateTimeString, getParsedData } from '../../voyage-eta-change-review/src/helper';
import { ScheduleChangeEvents } from '../../../src/models/schedule-change-model-impl';
import { ISTRINITYENABLED, ISWEATHERROUTINGENABLED } from '../../../src/models/feature-flag-model-impl';
import { FeatureFlagModel } from '../../../src/models/feature-flag-model';
import { showCoachMark } from '../../../src/utils/coach-marks-utils';
const TrinityOn = (<any>window)["appenv"]?.featureToggles?.trinityOn;
@customElement('voyage-vessel-details-menu')
export class VoyageVesselDetailsMenu extends LitElement
{
    @property({type: Boolean}) _showPassagePlanEntry: boolean = false;
    @property({type: Boolean}) _showViewRoute: boolean = false;
    @property({type: String}) planStatus?: string;
    @property({type: Object}) scheduleEntry: ScheduleEntry|null = null;
    @property({attribute: false}) plan: VoyagePlan|null = null;
    private _showManualWrRequestButton: boolean = false;
    private _showWeatherUpdateRequestButton: boolean = false;
    private _disableManualWrRequest: boolean = false;
    private _disabledTooltip : string = '';
    private _showWrPdfButton: boolean = false;
    private _wrPdfId: string | null = null;
    private _WeatherRoutingOn: boolean = false;
    private _TrinityOn: boolean = false;
    private _coachMarks: ReturnType<typeof showCoachMark> | null = null;
    @property({type: Boolean}) recalculating: boolean = false;
    @property({type: Boolean}) showEtaRequest: boolean = true;
    @property({type: Boolean}) showEtaRequestButton?: boolean = false;
    @property({attribute: false}) etaRequestText: TemplateResult | string | typeof nothing = nothing;
    @property({type: String}) etaRequestTime: string = '';
    @property({type: Boolean}) hasEtaRequestAlert: boolean = false;

    static get styles()
    {
        return [
            css`
                .navigation {
                    display: flex;
                    justify-content: center;
                    position: relative;
                    gap: 12px;
                }
                mc-button::part(button) {
                    color: #FFFFFF;
                }

                #tide-icon {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }

                #tide-icon svg{
                    width: 18px;
                }

                #weather-routing-icon {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }

                #weather-routing-icon svg{
                    width: 18px;
                }

                #weather-routing-pdf-icon svg{
                    width: 18px;
                }

                #weather-routing-pdf-icon {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }

                .eta-request-text {
                    width: 100%;
                }

                #eta-request-btn.highlight::part(button) {
                    box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.8), 0 0 0 6px rgba(0, 115, 171, 0.9);
                    border-radius: 4px;
                    transition: box-shadow 0.3s ease;
                }
            `
        ];
    }

    constructor()
    {
        super();
        this._updateFeatureFlags = this._updateFeatureFlags.bind(this);
        this._handleShowWrPdf = this._handleShowWrPdf.bind(this);
        
        this._WeatherRoutingOn = localStorage.getItem(ISWEATHERROUTINGENABLED) === "true";
        if(this._WeatherRoutingOn) {
            this.checkIfWrPdfButtonRequired(this.plan);
        }
        this._TrinityOn = localStorage.getItem(ISTRINITYENABLED) === "true";
        if(this._TrinityOn) {
            this.fetchETARequestAlert();
            this.fetchETARequestAlert = this.fetchETARequestAlert.bind(this);
        }
    }
    
    private _updateFeatureFlags() {
        const wasWeatherRoutingEnabled = this._WeatherRoutingOn;
        const wasTrinityEnabled = this._TrinityOn;
        
        this._WeatherRoutingOn = localStorage.getItem(ISWEATHERROUTINGENABLED) === "true";
        this._TrinityOn = localStorage.getItem(ISTRINITYENABLED) === "true";
        
        // If feature flags just got enabled, initialize their functionality
        if (!wasWeatherRoutingEnabled && this._WeatherRoutingOn) {
            this.checkIfWrPdfButtonRequired(this.plan);
            this.checkIfWRrwquestButtonRequired(this.plan);
        }
        
        if (!wasTrinityEnabled && this._TrinityOn) {
            this.fetchETARequestAlert();
            // Re-add the event listener now that the model is available
            if (ScheduleChangeModel && this.showEtaRequestButton) {
                ScheduleChangeModel.addEventListener(ScheduleChangeEvents.GetLatestEtaRequest, this.fetchETARequestAlert);
            }
        }
        
        this.requestUpdate();
    }

    updated(changedProperties: PropertyValues) {
        changedProperties.forEach((oldValue, propName) => {
            if (propName === "plan") {
                this.plan = this.plan;
                this._showPassagePlanEntry = !!(this.plan && this.plan.selectedRouteIndex >= 0 && this.plan.routes[this.plan.selectedRouteIndex]);
                if(this._WeatherRoutingOn) {
                    this.checkIfWRrwquestButtonRequired(this.plan);
                    this.checkIfWrPdfButtonRequired(this.plan);
                }
            }
            if (propName === "planStatus") {
                this._showViewRoute = (this.planStatus === "approved");;
            }
            if(propName === "scheduleEntry" && this._TrinityOn) {
                this.fetchETARequestAlert();
            }
            // --- Temp coachmark logic ---
            // if (propName === "showEtaRequestButton" && this._TrinityOn && this.showEtaRequestButton && !oldValue) {
            //     // Only show the coach mark if the user has not dismissed it before.
            //     // The 'seen' callback below sets this flag in localStorage so it
            //     // never appears again after the first interaction.
            //     const alreadySeen = localStorage.getItem('coachmark-eta-request-seen') === 'true';
            //     if (!alreadySeen) {
            //         this.updateComplete.then(() => this._startEtaRequestCoachMark());
            //     }
            // }
        });
    }


    private async checkIfWrPdfButtonRequired(plan: VoyagePlan | null) {
        if(!plan || plan.status !== 'approved') {
            return;
        }

        const pdfisAvailable = await VoyageWeatherRoutingModel?.checkIfPdfisAvailable(plan.departureTerminalCode);
        this._wrPdfId = pdfisAvailable ?? null;

        if(!pdfisAvailable || !plan.wr_enabled) {
            return;
        }

        this._showWrPdfButton = true;
        this.requestUpdate();
    }

    async checkIfWRrwquestButtonRequired(plan: VoyagePlan | null) {
        if(!plan) {
            return;
        }

        if(plan.status !== 'approved') {
            this._showManualWrRequestButton = false;
            this._showWeatherUpdateRequestButton = false;
            return;
        }

        // check if plan etd is greater than 72 hours
        const etd = new Date(plan.etd);
        const now = new Date();
        const diff = etd.getTime() - now.getTime();

        // check if plan eta is less than now
        const eta = new Date(plan.eta);
        const diffEta = eta.getTime() - now.getTime();

        if(diff > 72 * 60 * 60 * 1000 || diffEta < 0) {
            this._showManualWrRequestButton = false;
            this._showWeatherUpdateRequestButton = false;
            return;
        }

        const eospReport = await WeatherRoutingModel!.checkIfReportExists(plan.scheduleId, 'Arrival');

        if(eospReport?.isSubmitted) {
            this._showManualWrRequestButton = false;
            this._showWeatherUpdateRequestButton = false;

            return;
        }

        const selectedRoute = plan.routes[plan.selectedRouteIndex];
        const selectedRouteWeatherRoutingInfo = selectedRoute?.weatherRoutingInfo;
        this._showManualWrRequestButton = plan.status === "approved" && plan.eta > new Date().getTime() 
                                            && selectedRoute.routeCreatedBy !== 'WR'
                                            && !selectedRouteWeatherRoutingInfo?.required;
        this._showWeatherUpdateRequestButton = plan.status === "approved" && plan.eta > new Date().getTime()
                                                && (selectedRouteWeatherRoutingInfo?.required || (selectedRouteWeatherRoutingInfo?.is_original ?? false))  
                                                && !!(selectedRouteWeatherRoutingInfo?.received_at) 
                                                && (new Date(selectedRouteWeatherRoutingInfo?.received_at ?? '') < new Date());
        this._disableManualWrRequest = this.shouldDisableManualWrRequest(plan); 
        this.requestUpdate();
    }

    private shouldDisableManualWrRequest(plan: VoyagePlan):boolean {
        const selectedRoute = plan.routes[plan.selectedRouteIndex];
        const selectedRouteWeatherRoutingInfo = selectedRoute?.weatherRoutingInfo;
        const isWrRequired = selectedRouteWeatherRoutingInfo?.required ?? false;
        const isWrRequiredReasonManual = selectedRouteWeatherRoutingInfo?.required_reason === 'Manual';
        const isWrRequestedAt = selectedRouteWeatherRoutingInfo?.requested_at ? new Date(selectedRouteWeatherRoutingInfo?.requested_at!).getTime() : 0;
        const isNow = new Date().getTime();

        const updated_at = selectedRouteWeatherRoutingInfo?.received_at ? new Date(selectedRouteWeatherRoutingInfo?.received_at!).getTime() : 0;
        const gotWrAdviceAfterOurLastRequest = updated_at > isWrRequestedAt;

        const isWeatherUpdateRequest = selectedRoute.routeCreatedBy === 'WR';

        this._disabledTooltip = `Weather routing ${isWeatherUpdateRequest ? "update" : "request"} already requested at ${new Date(isWrRequestedAt).toUTCString()} UTC`;

        // if WR is required and the reason is manual and the request was made less than +/- 12 hours ago
        return !gotWrAdviceAfterOurLastRequest && isWrRequired && isWrRequiredReasonManual && Math.abs(isWrRequestedAt - isNow) < 12 * 60 * 60 * 1000;
    }
    connectedCallback() {
        super.connectedCallback();
        VoyageRoutingModel!.addEventListener('calculation-failed', ()=>{this.recalculating = false;});  
        if(ScheduleChangeModel && this.showEtaRequestButton)
            ScheduleChangeModel.addEventListener(ScheduleChangeEvents.GetLatestEtaRequest, this.fetchETARequestAlert);
        FeatureFlagModel?.addEventListener('featureFlagsUpdated', this._updateFeatureFlags);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        VoyageRoutingModel!.removeEventListener('calculation-failed', ()=>{this.recalculating = false;});  
        if(ScheduleChangeModel && this.showEtaRequestButton)
            ScheduleChangeModel.removeEventListener(ScheduleChangeEvents.GetLatestEtaRequest, this.fetchETARequestAlert);
        FeatureFlagModel?.removeEventListener('featureFlagsUpdated', this._updateFeatureFlags);
    }

    render() {
        // @ts-ignore
        const enableTides = (<any>window)["appenv"]?.featureToggles?.tides;
        return html`
            <div class="navigation">
                <mc-tooltip width="150">
                    <mc-button
                        label="Button"
                        hiddenlabel
                        appearance="neutral"
                        fit="small"
                        padding="none"
                        icon="pencil"
                        @click=${() => this._emitGsisEvent("edit-plan")}
                        slot="trigger"
                        data-test="edit-plan"
                        ?disabled="${this.recalculating}"
                        >
                    </mc-button>
                    <div>
                        ${this.planStatus !== "" ? "Edit Route" : "Add Route"}
                    </div>
                </mc-tooltip> 
                ${this._showViewRoute ? html` <mc-tooltip width="150">
                    <mc-button
                        label="Button"
                        hiddenlabel
                        appearance="neutral"
                        fit="small"
                        padding="none"
                        icon="eye"
                        @click=${() => this._emitGsisEvent("view-plan")}
                        slot="trigger"
                        data-test="view-plan"
                        ?disabled="${this.recalculating}"
                        >
                    </mc-button>
                    <div>
                        View route
                    </div>
                </mc-tooltip> 
                ` : nothing}
                <mc-tooltip width="150">
                    <mc-button
                        label="Button"
                        hiddenlabel
                        appearance="neutral"
                        fit="small"
                        padding="none"
                        icon="clipboard-list-text"
                        @click=${() => this._emitGsisEvent("show-port-memo")}
                        slot="trigger"
                        data-test="port-memo"
                        ?disabled="${this.recalculating}"
                        >
                    </mc-button>
                    <div>
                        Port memo
                    </div>
                </mc-tooltip> 
                ${enableTides ? html`<mc-tooltip width="150">
                    <mc-button
                        label="Button"
                        hiddenlabel
                        appearance="neutral"
                        fit="small"
                        padding="none"
                        @click=${() => this._emitGsisEvent("show-tide-information")}
                        slot="trigger"
                        data-test="tide-information"
                        ?disabled="${this.recalculating}"
                        >
                        <span slot="icon">
                            <div id="tide-icon"> 
                                ${SvgIcons.customTide}
                            </div>
                        </span>
                    </mc-button>
                    <div>
                        Tide
                    </div>
                </mc-tooltip>` : nothing}
                ${this._showPassagePlanEntry ? html`<mc-tooltip width="150">
                    <mc-button
                        label="Button"
                        hiddenlabel
                        appearance="neutral"
                        fit="small"
                        padding="none"
                        @click=${() => this._emitGsisEvent("show-passage-plan")}
                        slot="trigger"
                        icon="file-eye"
                        ?disabled="${this.recalculating}"
                        >
                    </mc-button>
                    <div>
                        Passage plan
                    </div>
                </mc-tooltip>` : nothing}
                ${this._TrinityOn && this.showEtaRequestButton ? html`<mc-tooltip width="150">
                    <mc-button
                        id="eta-request-btn"
                        label="Button"
                        hiddenlabel
                        appearance="neutral"
                        fit="small"
                        padding="none"
                        @click=${() => this._emitGsisEvent("show-eta-request")}
                        slot="trigger"
                        ?disabled="${!this.showEtaRequest || this.recalculating}"
                        >
                        <span slot="icon">
                            <div id="tide-icon"> 
                                ${SvgIcons.calenderClock}
                            </div>
                        </span>
                    </mc-button>
                    <div class="eta-request-text">
                        ${this.hasEtaRequestAlert ? html`${this.etaRequestText}` : html`Request new ETA <i>${this.etaRequestText}</i>`}
                    </div>
                </mc-tooltip>` : nothing} 
                ${(this._showManualWrRequestButton || this._showWeatherUpdateRequestButton) && this._WeatherRoutingOn ? html`<mc-tooltip width="200">
                <mc-button
                        label="Button"
                        hiddenlabel
                        appearance="neutral"
                        fit="small"
                        padding="none"
                        .disabled=${this._disableManualWrRequest}
                        @click=${() => this._handleManualRequest()}
                        slot="trigger"
                        data-test="tide-information"
                        >
                        <span slot="icon">
                            <div id="weather-routing-icon"> 
                                ${this._showWeatherUpdateRequestButton ? SvgIcons.weatherRoutingUpdateIcon : SvgIcons.weatherRoutingIcon}
                            </div>
                        </span>
                    </mc-button>
                    <div>
                        ${this._disableManualWrRequest ? this._disabledTooltip : this._showWeatherUpdateRequestButton ? 'Request WR update' : 'Request WR advice' }
                    </div>
                </mc-tooltip>
                ` : nothing}
                ${this._showWrPdfButton && this._WeatherRoutingOn ? html`<mc-tooltip width="150">
                    <mc-button
                        label="Button"
                        hiddenlabel
                        appearance="neutral"
                        fit="small"
                        padding="none"
                        @click=${this._handleShowWrPdf}
                        slot="trigger"
                        data-test="wr-pdf"
                        >
                        <span slot="icon">
                            <div id="weather-routing-pdf-icon"> 
                                ${SvgIcons.filePdf}
                            </div>
                        </span>
                    </mc-button>
                    <div>
                        View Latest WR PDF
                    </div>
                    </mc-tooltip>
                ` : nothing}
            </div>
        `;
    }

    private async _handleManualRequest() {
        if(this._showWeatherUpdateRequestButton) {
            VesselClfPopupModel!.setWRUpdatePopupVisibility(
             this.plan?.scheduleId,   
            );
        } else {
            const dialog = VoyageDialogPopupManager.createDialog({
               headerText: `Request Weather Routing advice`,
               bodyText: `Are you sure you want to request weather routing advice ?`,
               acceptButtonText: 'Yes and request',
                rejectButtonText: 'No',
            })
    
            const response = await dialog.getResponse();
    
            if(response === 'accept') {
                this._handleManualWrRequest();
            }
        }

    }

    private async _handleManualWrRequest() {
        
        if(!this.plan) {
            console.warn("No Plan found");
            return;
        }

        if(this.plan.selectedRouteIndex !== -1 && this.plan.routes[this.plan.selectedRouteIndex]?.weatherRoutingInfo) {
            this.plan.routes[this.plan.selectedRouteIndex].weatherRoutingInfo!.required = true;
            this.plan.routes[this.plan.selectedRouteIndex].weatherRoutingInfo!.required_reason = 'Manual';
        }

        await new Promise(resolve => setTimeout(resolve, 100)); // wait for the plan to be updated

        await VesselPlanningModel!.setPlan(this.plan.scheduleId, this.plan, true, true);
    }

    private _emitGsisEvent(name: string): void
    {
        this.dispatchEvent(new CustomEvent(name, { detail: this.scheduleEntry, composed: true, bubbles: true }));
    }

    private _handleShowWrPdf(e: Event): void {
        e.stopPropagation();
        e.preventDefault();
        if (this._wrPdfId) {
            VoyageWeatherRoutingModel?.showWrPdf(this._wrPdfId);
        }
    }

    private async fetchETARequestAlert() {
        if(!this.scheduleEntry?.active) return;
        const ScheduleChangeRequestAlert = await ScheduleChangeModel!.getCurrentAlert();
        if(!this.showEtaRequest && ScheduleChangeRequestAlert){
            this.etaRequestText = EtaRequestDeactivated.RequestFromTbm;
            return;
        }
        const alert = await ScheduleChangeModel!.getEtaRequestAlert();
        if (alert) {
            const data = getParsedData(alert.data);
            if(data.schedule_id !== this.scheduleEntry.id){
                // Alert does not belong to current active schedule
                return;
            }

            this.hasEtaRequestAlert = true;
            const etaPilot = await formatEtaDate(data.earliest_requested_eta.arrivalTimings.array[1].timestamp, data.arrival_terminal_code);
            const etaBerth = await formatEtaDate(data.earliest_requested_eta.arrivalTimings.array[0].timestamp, data.arrival_terminal_code);
            const etaRequestedTime = formatUTCDateTimeString(data.deviation_remark_timestamp);
            this.etaRequestText = html`
                Requested new ETA on ${etaRequestedTime}<br />
                ETA Pilot: <b>${etaPilot}</b><br />
                ETA Berth: <b>${etaBerth}</b>
            `;
        }
    }

    private _startEtaRequestCoachMark(): void {
        // Dismiss any existing coach mark before showing a new one.
        this._coachMarks?.hide();
        this._coachMarks = null;

        if (!this.shadowRoot) return;

        this._coachMarks = showCoachMark({
            debug: true,
            shadowRoot: this.shadowRoot,
            theme: 'dark',
            items: [{
                title: 'Request new ETA',
                text: 'Use this button to request a new Estimated Time of Arrival from the terminal booking manager.',
                // CSS selector resolved against this.shadowRoot
                target: '#eta-request-btn',
                seen: () => {
                    // Persist that the user has seen this coach mark so it is
                    // never shown again, even after a page reload.
                    localStorage.setItem('coachmark-eta-request-seen', 'true');
                    this._coachMarks = null;
                },
            }],
        });
    }
};

